/**
 * SPEC-08 §4.6 — `<ExplainerPanel/>`.
 *
 * Generalises the strong PAJE "Bu sayfa ne anlatır? / Kim kullanır?"
 * accordion into a reusable primitive. Auditors who know the surface never
 * have to expand it; juniors and SMMM candidates get a quick reference.
 *
 *   <ExplainerPanel
 *     id="paje"
 *     title={t("paje.guide.title")}
 *     defaultOpen={false}
 *   >
 *     ...sections...
 *   </ExplainerPanel>
 *
 * Visual: collapsible card with a muted background, lucide `info` icon,
 * `text-section` title in the trigger. Children render the body sections —
 * use plain `<p>` / `<section>` / `<ul>` markup; this component does not
 * impose section chrome beyond a top-level vertical stack.
 *
 * This SPEC ships the component and wires PAJE onto it as the reference
 * implementation (no content change to PAJE — the locale keys stay the
 * same). Per-tab explainer content lands in the per-tab SPECs.
 */

import { Info } from "lucide-react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";

export interface ExplainerPanelProps {
  /**
   * Unique id used as the Accordion item value AND the data-testid base
   * (e.g. `id="paje"` → `data-testid="paje-guide"` / `paje-guide-trigger"`
   * — preserves the existing PAJE explainer test-ids).
   */
  id: string;
  title: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
  className?: string;
}

export function ExplainerPanel({
  id,
  title,
  defaultOpen = false,
  children,
  className,
}: ExplainerPanelProps) {
  return (
    <Accordion
      type="single"
      collapsible
      defaultValue={defaultOpen ? "guide" : undefined}
      className={cn("rounded-md border bg-muted/30", className)}
      data-testid={`${id}-guide`}
    >
      <AccordionItem value="guide" className="border-0">
        <AccordionTrigger
          className="px-4 py-2 text-sm hover:no-underline"
          data-testid={`${id}-guide-trigger`}
        >
          <span className="inline-flex items-center gap-2 text-muted-foreground">
            <Info className="h-4 w-4" aria-hidden="true" />
            {title}
          </span>
        </AccordionTrigger>
        <AccordionContent className="px-4 pb-4 text-sm">
          <div className="space-y-4">{children}</div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
